import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { Spinner, Col, Row, Button, Icon, Text, Title, Space } from '../../src';
import { Accept, useDropzone } from 'react-dropzone';
import { mcDelete, mcNew } from '@mc/react-ui-icons';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { VideoThumbnail } from './VideoThumbnail';

export type ImageGalleryInputValue<VALUE> = VALUE[];

export type ImageMetaProps = {
  /**
   * Typ souboru a jeho formát (MIME type), např. image/jpeg, video/mp4 apod.
   */
  type?: string;
};

export type ImageGalleryFileInfo = {
  lastModified: number;
  name: string;
  size: number;
  contentType: string;
};

export type ImageGalleryInputProps<VALUE> = {
  onImageUpload: (
    image: Blob,
    fileInfo: ImageGalleryFileInfo
  ) => Promise<VALUE>;
  getImageUrl: (image: VALUE) => string;
  /**
   * Vrátí název obrázku.
   */
  getImageName?: (image: VALUE) => string;
  getImageMeta?: (image: VALUE) => ImageMetaProps;
  /**
   * Vrátí popis obrázku.
   * @param image
   */
  getImageDescription?: (image: VALUE) => string;
  onDelete?: (image: VALUE) => void;
  onChange: (val?: ImageGalleryInputValue<VALUE>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  value: ImageGalleryInputValue<VALUE>;
  maxFiles?: number;
  /*
   * akceptované typy souborů, defaultně: png, jpg a jpeg soubory (hodnota: { 'image/jpeg': [], 'image/png': [], })
   */
  accept?: Accept;
  className?: string;
  onUploadStart?: () => void;
  onUploadStop?: () => void;
};

/**
 * VALUE může být buď základní datový typ, např. string, nebo objekt, ve kterém j atribut id.
 */
export function ImageGalleryInput<VALUE>({
  getImageUrl,
  onImageUpload,
  onChange,
  value,
  readOnly = false,
  disabled = false,
  maxFiles = 0,
  accept = {
    'image/jpeg': [],
    'image/png': [],
    'video/mp4': [],
    'video/webm': [],
  },
  className,
  onUploadStart,
  onUploadStop,
  onDelete,
  getImageName,
  getImageMeta,
  getImageDescription,
}: ImageGalleryInputProps<VALUE>) {
  const [imageIndex, setImageIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [numberOfSpinners, setNumberOfSpinners] = useState<number>(0);
  const [upLoading, setUpLoading] = useState<boolean>(false);
  const currValue = useRef<ImageGalleryInputValue<VALUE>>([]);
  currValue.current = Array.isArray(value) ? value : [];
  let cls = classNames(className, Classes.IMAGE_GALLERY_INPUT, {});

  const getItemId = (item: VALUE) => {
    if (typeof item === 'object' && item !== null) {
      return (item as any).id;
    }
    return item;
  };

  const readFileSync = async (file: File): Promise<VALUE> => {
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onabort = () => console.log('file reading was aborted', file);
      reader.onerror = () => console.log('file reading has failed', file);
      reader.onload = () => {
        if (reader.result !== null) {
          const fileInfo: ImageGalleryFileInfo = {
            lastModified: file.lastModified,
            name: file.name,
            size: file.size,
            contentType: file.type,
          };
          onImageUpload(file, fileInfo).then((image) => {
            resolve(image);
            setNumberOfSpinners((prevSpinners) => prevSpinners - 1);
          });
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  useEffect(() => {
    if (numberOfSpinners != 0 && !upLoading) {
      setUpLoading(true);
      onUploadStart && onUploadStart();
    }
    if (numberOfSpinners == 0 && upLoading) {
      setUpLoading(false);
      onUploadStop && onUploadStop();
    }
  }, [numberOfSpinners]);
  const handleOnDrop = async (files: File[]) => {
    setNumberOfSpinners((prevSpinners) => prevSpinners + files.length);

    files.forEach((file) => {
      readFileSync(file).then((id) => {
        // Ensure that the latest value is used for each onChange call
        const currItems = currValue.current || [];
        onChange && onChange([...currItems, id]);
      });
    });
  };

  const isVideo = (id: VALUE) => {
    const type = getImageMeta?.(id)?.type ?? '';
    return type.toLowerCase().startsWith('video/');
  };

  const handleDelete = async (e: SyntheticEvent, image: VALUE) => {
    e.stopPropagation();
    const currItems = currValue.current || [];
    onDelete?.(image);
    onChange(currItems.filter((x) => getItemId(x) !== getItemId(image)));
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept,
    maxFiles: maxFiles,
    preventDropOnDocument: true,
    onDrop: handleOnDrop,
    disabled: disabled || readOnly,
  });
  const currItems = currValue.current || [];
  const renderPreviews = currItems.map((fileId, index) => {
    const isVideoBool = isVideo(fileId);

    const imageName = getImageName?.(fileId);
    const imageDescription = getImageDescription?.(fileId);
    return (
      <div
        className={Classes.THUMB}
        key={index}
        onDragStart={(e) => e.preventDefault()}
        title={imageDescription}
      >
        <div
          className={Classes.THUMB_INNER}
          onClick={(e) => {
            setIsOpen(true);
            setImageIndex(index);
            e.stopPropagation();
          }}
        >
          {isVideoBool ? (
            <VideoThumbnail />
          ) : (
            <img src={getImageUrl(fileId)} draggable="false" />
          )}
          <Button
            disabled={disabled || readOnly}
            danger
            icon={<Icon icon={mcDelete} />}
            className={Classes.THUMB_DELETE}
            onClick={(e) => handleDelete(e, fileId)}
          />
        </div>
        {imageName && (
          <div title={imageName} className={Classes.THUMB_NAME}>
            {imageName}
          </div>
        )}
      </div>
    );
  });
  const TempSpinners = () => {
    if (numberOfSpinners < 1) {
      return <></>;
    }
    return (
      <>
        {Array.from(Array(numberOfSpinners).keys()).map((index) => (
          <div className={Classes.THUMB} key={index}>
            <div
              className={classNames(
                Classes.THUMB_INNER,
                Classes.THUMB_INNER_SPINNER
              )}
            >
              <Spinner />
            </div>
          </div>
        ))}
      </>
    );
  };

  const imageLoadErrorMessage = (fileId: VALUE) => {
    // Protože Lightbox nepodporuje video ani nemá vlastní přetížení zobrazení,
    // můžeme to řešit až při odchytu chyby při neúspěšném načtení obrázku.
    return isVideo(fileId) ? (
      <video src={getImageUrl(fileId)} controls draggable="false" />
    ) : (
      <div>Obrázek se nepodařilo načíst</div>
    );
  };

  return (
    <div className={cls}>
      <Row>
        <Col>
          {isOpen && (
            <Lightbox
              imageCaption={getImageDescription?.(value[imageIndex])}
              imageTitle={getImageName?.(value[imageIndex])}
              mainSrc={getImageUrl(value[imageIndex])}
              nextSrc={getImageUrl(value[(imageIndex + 1) % value.length])}
              prevSrc={getImageUrl(
                value[(imageIndex + value.length - 1) % value.length]
              )}
              onCloseRequest={() => setIsOpen(false)}
              onMovePrevRequest={() =>
                setImageIndex((imageIndex + value.length - 1) % value.length)
              }
              onMoveNextRequest={() =>
                setImageIndex((imageIndex + 1) % value.length)
              }
              imageLoadErrorMessage={imageLoadErrorMessage(value[imageIndex])}
              reactModalStyle={{ overlay: { zIndex: 42000 } }}
            />
          )}

          <div
            {...getRootProps()}
            className={classNames(Classes.DROPZONE, {
              [Classes.EMPTY_DROPZONE]:
                currItems.length < 1 && numberOfSpinners < 1,
              [Classes.DRAGGING_DROPZONE]: isDragActive,
            })}
          >
            {isDragActive && !disabled && !readOnly && (
              <Title
                className={Classes.DRAG_TEXT}
                type={'card'}
                intent={'inactive'}
              >
                Sem přetáhněte soubory
              </Title>
            )}
            <aside className={Classes.THUMBS_CONTAINER}>
              {currItems.length > 0 || numberOfSpinners > 0 ? (
                <>
                  {renderPreviews}
                  <TempSpinners />
                  {readOnly || disabled || isDragActive || (
                    <div
                      className={classNames(
                        Classes.THUMB,
                        Classes.THUMB_BUTTON
                      )}
                    >
                      <input {...getInputProps()} />

                      <Text intent={'primary'} className={'my-auto'}>
                        <Icon size={'2x'} icon={mcNew} />
                      </Text>
                    </div>
                  )}
                </>
              ) : (
                <Space vertical size={'2'}>
                  <Title type={'card'} intent={'inactive'}>
                    Sem přetáhněte soubory
                  </Title>
                  {!isDragActive && (
                    <Button
                      disabled={disabled || readOnly}
                      disableStopPropagation
                      style={'link'}
                    >
                      Přidat z počítače
                    </Button>
                  )}
                  <input {...getInputProps()} />
                </Space>
              )}
            </aside>
          </div>
        </Col>
      </Row>
    </div>
  );
}
